import { useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import { EntityRefChips } from '../common/EntityRefChips';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material/styles';
import { getThemeTokens } from '../../theme/tokens';
import { SCOPE_PROFILES_UI } from './scopeProfiles';
import { useAdminTokens, useAdminActions } from './useApiTokens';

interface Props {
  refreshKey: number;
  onRefresh: () => void;
}

export function AdminTokensSection({ refreshKey, onRefresh }: Props) {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  const { value: tokenList, loading, error } = useAdminTokens(refreshKey);
  const { revokeToken, revokeAll, loading: actionLoading } = useAdminActions();

  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [revokeAllResult, setRevokeAllResult] = useState<number | null>(null);

  const handleRevokeToken = async (id: string) => {
    await revokeToken(id);
    onRefresh();
  };

  const handleRevokeAll = async () => {
    const count = await revokeAll();
    setRevokeAllResult(count);
    setConfirmRevokeAll(false);
    onRefresh();
  };

  const formatDate = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  return (
    <InfoCard
      title="Admin: All Tokens"
      subheader="As a member of an admin group, you can revoke any token or all tokens at once (security incident kill-switch). Revocation propagates within ~30 seconds."
      action={
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => setConfirmRevokeAll(true)}
          disabled={actionLoading || loading}
          sx={{ mt: 1, mr: 1 }}
        >
          Revoke ALL tokens
        </Button>
      }
    >
      {revokeAllResult !== null && (
        <Typography variant="body2" color="success.main" mb={2}>
          {revokeAllResult} token(s) revoked.
        </Typography>
      )}

      {loading && <CircularProgress size={24} />}
      {error && <Typography color="error">{error.message}</Typography>}

      {!loading && !error && tokenList && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokenList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color={tokens.textMuted}>
                      No tokens.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tokenList.map(token => {
                  const isRevoked = !!token.revokedAt;
                  const isExpired =
                    !isRevoked &&
                    !!token.expiresAt &&
                    new Date(token.expiresAt) < new Date();
                  const scopeLabels = token.scopes
                    .map(
                      s => SCOPE_PROFILES_UI.find(p => p.id === s)?.label ?? s,
                    )
                    .join(', ');

                  return (
                    <TableRow
                      key={token.id}
                      sx={{ opacity: isRevoked || isExpired ? 0.5 : 1 }}
                    >
                      <TableCell>
                        <Typography variant="body2" noWrap maxWidth={180}>
                          {token.name}
                        </Typography>
                        <Typography variant="caption" color={tokens.textMuted}>
                          {token.tokenPrefix}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <EntityRefChips refs={[token.subjectRef]} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{scopeLabels}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(token.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(token.lastUsedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isRevoked ? (
                          <Chip
                            label="Revoked"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ) : isExpired ? (
                          <Chip
                            label="Expired"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell padding="checkbox">
                        {!isRevoked && (
                          <Tooltip title="Revoke this token">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRevokeToken(token.id)}
                              disabled={actionLoading}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Revoke-all confirmation dialog */}
      <Dialog
        open={confirmRevokeAll}
        onClose={() => setConfirmRevokeAll(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke ALL tokens?</DialogTitle>
        <DialogContent>
          <Typography>
            This will immediately revoke <strong>every</strong> active API token
            across all users and teams. Use this for security incidents only.
            <br />
            <br />
            Propagation delay: up to ~30 seconds.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevokeAll(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevokeAll}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={16} /> : 'Revoke ALL'}
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
}
